// Клиент REST API авторизации. Сессия живёт в httpOnly-куке, поэтому здесь нет
// ни токенов, ни их хранения: браузер подставляет куку сам.
import { request } from './request';

export type { ApiError, ApiResult } from './request';
import type { ApiResult } from './request';

export type UserRole = 'user' | 'admin';
export type UserStatus = 'active' | 'blocked';

/**
 * Персональные лимиты, выданные админкой. Отсутствие ключа (или всего объекта)
 * значит «лимит по умолчанию»; применяет их сервер, клиент только показывает.
 */
export interface UserLimits {
  maxVariants?: number;
  aiDaily?: number;
  magicDaily?: number;
  maxTrips?: number;
  /** Вложения в саппорт-чате — включается админом персонально. */
  chatFiles?: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  emailVerified: boolean;
  createdAt: number;
  lastLoginAt: number | null;
  limits: UserLimits | null;
  /** Мягкое удаление: сам пользователь этого поля никогда не увидит (сессия
   *  удалённого аккаунта недействительна) — оно нужно только админке, которая
   *  запрашивает пользователей в обход обычного фильтра. */
  deletedAt: number | null;
}

/**
 * `impersonator` заполнен, когда в аккаунте сидит администратор, вошедший «от
 * имени» этого пользователя (см. impersonate ниже). Обычная сессия отдаёт null.
 */
export function fetchMe(): Promise<ApiResult<{ user: AuthUser; impersonator: AuthUser | null }>> {
  return request('/api/auth/me');
}

export function register(input: {
  email: string;
  password: string;
  name: string;
  lang: string;
}): Promise<ApiResult<{ user: AuthUser }>> {
  return request('/api/auth/register', { method: 'POST', json: input });
}

export function login(input: {
  email: string;
  password: string;
}): Promise<ApiResult<{ user: AuthUser }>> {
  return request('/api/auth/login', { method: 'POST', json: input });
}

export function logout(): Promise<ApiResult<void>> {
  return request('/api/auth/logout', { method: 'POST' });
}

export function forgotPassword(input: { email: string; lang: string }): Promise<ApiResult<void>> {
  return request('/api/auth/forgot', { method: 'POST', json: input });
}

export function resetPassword(input: {
  token: string;
  password: string;
}): Promise<ApiResult<void>> {
  return request('/api/auth/reset', { method: 'POST', json: input });
}

export function verifyEmail(token: string): Promise<ApiResult<{ user: AuthUser }>> {
  return request('/api/auth/verify', { method: 'POST', json: { token } });
}

export function resendVerification(lang: string): Promise<ApiResult<void>> {
  return request('/api/auth/verify/resend', { method: 'POST', json: { lang } });
}

export function changePassword(input: {
  current: string;
  next: string;
}): Promise<ApiResult<void>> {
  return request('/api/auth/password', { method: 'POST', json: input });
}

export function updateName(name: string): Promise<ApiResult<{ user: AuthUser }>> {
  return request('/api/auth/me', { method: 'PATCH', json: { name } });
}

/**
 * Мягкое удаление своего аккаунта: сессия закрывается сервером сразу же.
 * Данные (поездки, снимки) не стираются — их возвращает только restoreAccount.
 */
export function deleteAccount(): Promise<ApiResult<void>> {
  return request('/api/auth/account', { method: 'DELETE' });
}

/**
 * Восстановление удалённого аккаунта старым паролем — единственный способ
 * вернуть данные после deleteAccount(). Сервер отвечает 'restorable' на
 * попытку register() с той же почтой; отсюда и вызывается эта функция.
 */
export function restoreAccount(
  email: string,
  password: string,
): Promise<ApiResult<{ user: AuthUser }>> {
  return request('/api/auth/restore', { method: 'POST', json: { email, password } });
}

/**
 * Вход администратора в аккаунт пользователя — чтобы починить его поездку
 * руками, а не по описанию в переписке. Ручки админские, но действие это
 * сессионное, поэтому живёт здесь, рядом с login/logout.
 *
 * Собственная сессия администратора при этом закрывается: кука одна, и
 * вернуться в свой аккаунт можно только через stopImpersonation().
 */
export function impersonate(
  userId: string,
): Promise<ApiResult<{ user: AuthUser; impersonator: AuthUser }>> {
  return request('/api/admin/impersonate', { method: 'POST', json: { userId } });
}

export function stopImpersonation(): Promise<ApiResult<{ user: AuthUser }>> {
  return request('/api/admin/impersonate/stop', { method: 'POST' });
}
